import { useMemo } from 'react';
import { useGetLineManagerAnalyticsQuery } from '../../../store/services/report.service';

/**
 * Custom hook for line manager analytics with enhanced functionality
 * Follows the same pattern as useDepartmentAnalytics
 * @param {Object} params - Parameters for the analytics query
 * @param {number} params.tenancyId - The tenancy ID
 * @param {string} params.startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} params.endDate - End date in YYYY-MM-DD format (optional)
 * @param {number} params.lineManagerId - Specific line manager ID to filter by (optional)
 * @param {boolean} params.skip - Whether to skip the query (optional)
 * @returns {Object} Enhanced analytics data and utilities
 */
export const useLineManagerAnalytics = ({
    tenancyId,
    startDate,
    endDate,
    lineManagerId,
    includeHierarchy = false,
    skip = false
}) => {
    // Calculate default dates (past 6 months) if not provided
    const defaultDates = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);

        return {
            defaultStartDate: sixMonthsAgo.toISOString().split('T')[0],
            defaultEndDate: today.toISOString().split('T')[0]
        };
    }, []);

    // Use provided dates or fall back to defaults
    const queryStartDate = startDate || defaultDates.defaultStartDate;
    const queryEndDate = endDate || defaultDates.defaultEndDate;

    const {
        data: rawData,
        error,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        refetch
    } = useGetLineManagerAnalyticsQuery(
        {
            tenancyId,
            startDate: queryStartDate,
            endDate: queryEndDate,
            lineManagerId,
            includeHierarchy
        },
        { skip: skip || !tenancyId }
    );

    // Memoized processed data
    const processedData = useMemo(() => {
        if (!rawData) {
            return {
                summary: {
                    totalDepartments: 0,
                    totalLocations: 0,
                    totalLineManagers: 0,
                    totalUsers: 0,
                    totalCoursesSent: 0,
                    totalCoursesCompleted: 0,
                    totalSimulationsSent: 0,
                    totalSimulationsClicked: 0,
                    overallCourseCompletionRate: 0,
                    overallSimulationClickRate: 0
                },
                lineManagerData: [],
                directReportsData: []
            };
        }

        const { summary, lineManagerData, directReportsData, ...rest } = rawData;

        // Calculate additional metrics for summary
        const enhancedSummary = {
            ...summary,
            overallCourseCompletionRate: summary?.totalCoursesSent > 0
                ? Math.round((summary.totalCoursesCompleted / summary.totalCoursesSent) * 100 * 100) / 100
                : 0,
            overallSimulationClickRate: summary?.totalSimulationsSent > 0
                ? Math.round((summary.totalSimulationsClicked / summary.totalSimulationsSent) * 100 * 100) / 100
                : 0
        };

        const enhancedLineManagerData = (lineManagerData || [])
            .map(lm => ({
                ...lm,
                efficiency: lm.userCount > 0
                    ? Math.round(((lm.coursesCompleted + lm.simulationsClicked) / lm.userCount) * 100) / 100
                    : 0,
                courseCompletionRate: lm.coursesSent > 0
                    ? Math.round((lm.coursesCompleted / lm.coursesSent) * 100 * 100) / 100
                    : 0,
                simulationClickRate: lm.simulationsSent > 0
                    ? Math.round((lm.simulationsClicked / lm.simulationsSent) * 100 * 100) / 100
                    : 0
            }))
            .sort((a, b) => b.efficiency - a.efficiency);

        const enhancedDirectReportsData = (directReportsData || [])
            .map(report => ({
                ...report,
                courseCompletionRate: report.coursesSent > 0
                    ? Math.round((report.coursesCompleted / report.coursesSent) * 100 * 100) / 100
                    : 0,
                simulationClickRate: report.simulationsSent > 0
                    ? Math.round((report.simulationsClicked / report.simulationsSent) * 100 * 100) / 100
                    : 0,
                overallPerformance:
                    ((report.coursesSent > 0 ? (report.coursesCompleted / report.coursesSent) : 0) +
                        (report.simulationsSent > 0 ? (report.simulationsClicked / report.simulationsSent) : 0)) / 2 * 100
            }))
            .sort((a, b) => b.overallPerformance - a.overallPerformance);

        return {
            ...rest,
            summary: enhancedSummary,
            lineManagerData: enhancedLineManagerData,
            directReportsData: enhancedDirectReportsData
        };
    }, [rawData]);

    // Analytics utilities
    const analytics = useMemo(() => {
        if (!processedData) return {};

        const { lineManagerData, directReportsData, summary } = processedData;

        return {
            topLineManagerByCompletion: (lineManagerData && lineManagerData.length > 0) ? lineManagerData[0] : null,
            topDirectReportByPerformance: (directReportsData && directReportsData.length > 0) ? directReportsData[0] : null,

            // Line Manager insights
            lineManagerInsights: {
                averageCompletionRate: (lineManagerData && lineManagerData.length > 0)
                    ? lineManagerData.reduce((acc, lm) => acc + lm.courseCompletionRate, 0) / lineManagerData.length
                    : 0,
                averageClickRate: (lineManagerData && lineManagerData.length > 0)
                    ? lineManagerData.reduce((acc, lm) => acc + lm.simulationClickRate, 0) / lineManagerData.length
                    : 0,
                managersAboveAverage: (lineManagerData || []).filter(lm =>
                    lm.courseCompletionRate > (summary?.overallCourseCompletionRate || 0)
                ).length
            },

            // Direct Reports insights  
            directReportsInsights: {
                averageCompletionRate: (directReportsData && directReportsData.length > 0)
                    ? directReportsData.reduce((acc, dr) => acc + dr.courseCompletionRate, 0) / directReportsData.length
                    : 0,
                averageClickRate: (directReportsData && directReportsData.length > 0)
                    ? directReportsData.reduce((acc, dr) => acc + dr.simulationClickRate, 0) / directReportsData.length
                    : 0,
                reportsAboveAverage: (directReportsData || []).filter(dr =>
                    dr.overallPerformance > 50
                ).length
            }
        };
    }, [processedData]);

    return {
        // Data
        data: processedData,
        rawData,
        analytics,

        // Query states
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,

        // Actions
        refetch
    };
};

/**
 * Hook for line manager analytics with automatic date range (previous month)
 * @param {number} tenancyId - The tenancy ID
 * @param {number} lineManagerId - Line manager ID (optional)
 * @returns {Object} Analytics data with default date range
 */
export const useLineManagerAnalyticsDefault = (tenancyId, lineManagerId) => {
    const { startDate, endDate } = useMemo(() => {
        const currentDate = new Date();
        const firstDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonth.getTime() - 1);
        const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), 1);

        return {
            startDate: firstDayOfPreviousMonth.toISOString().split('T')[0],
            endDate: firstDayOfCurrentMonth.toISOString().split('T')[0]
        };
    }, []);

    return useLineManagerAnalytics({ tenancyId, startDate, endDate, lineManagerId });
};

export default useLineManagerAnalytics;